import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as yup from "yup";
import api from "../../apiClient";
import { auth } from "../../firebase/firebaseConfig";
import ActivataionAccount from "./ActivataionAccount";
import SocialButtons from "./sessions/SocialButtons";
import TextField from "./sessions/TextField";

const UserSignin = () => {
  const navigate = useNavigate();
  const validationSchema = yup.object().shape({
    userEmail: yup
      .string()
      .email("Invalid email")
      .required("Email is required"),
    password: yup
      .string()
      .min(8, "Password must be 8 characters long")
      .required("Password is required"),
  });
  const [showActivationModal, setShowActivationModal] = useState(false); // State to control modal visibility
  const [userDeactivtaeErrorMsg, setUserDeactivateErrorMsg] = useState("");

  const [userEmail, setUserEmail] = useState(""); // State to hold user email

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Add state for password visibility
  const initialValues = { userEmail: "", password: "" };
  const location = useLocation();
  const companyId = location.state?.companyId;
  const companyName = location.state?.companyName;

  const [loading, setLoading] = useState(false); // State to manage loading

  const handleUserActivation = async (values) => {
    try {
      // Check if the user exists
      const checkUserResponse = await api.checkUser(values.userEmail);

      if (checkUserResponse.status === 200) {
        if (checkUserResponse.data.deActivate === true) {
          setErrorMessage(""); // Clear any previous error message
          setUserEmail(values.userEmail); // Set the user email
          setUserDeactivateErrorMsg(
            <span>
              Your email already exists. Please{" "}
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault(); // Prevent default link behavior
                  localStorage.setItem("redirectAfterLogin", "signin");
                  setShowActivationModal(true); // Show the activation modal
                }}
              >
                Activate Your Account
              </Link>{" "}
              to log in to your account.
            </span>
          );
        } else {
          // If the user is already active, proceed to login
          handleLogin(values);
        }
      } else {
      }
    } catch (error) {
      if (error.response) {
      } else {
      }
    }
  };
  const handleLogin = async (values) => {
    let loadingPopup;

    // Show loading message using SweetAlert
    loadingPopup = Swal.fire({
      title: "Signing in...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await api.userLogin(values.userEmail, values.password);
      const user = response.data.user;
      const errorMessage = response.data.errorMessage;

      if (errorMessage) {
        // If there's an error message returned by the API, set it to display on the UI
        setErrorMessage(errorMessage);
        return; // Exit the function early if there's an error
      }

      // Store user info locally (but not the token)
      localStorage.setItem("user", JSON.stringify(user));

      const token = response.data.token;
      // Save user and token in localStorage (or in a global state like context)
      localStorage.setItem("token", token);

      // Setting cookies for user and token to be accessible across all subdomains
      const domain =
        window.location.hostname === "localhost" ? "localhost" : "jobbox.one";
      document.cookie = `user=${JSON.stringify(
        user
      )}; path=/; domain=${domain}; secure; samesite=strict;`;
      document.cookie = `token=${token}; path=/; domain=${domain}; secure; samesite=strict;`;

      if (user !== null) {
        if (user.userRole === "Candidate") {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "candidate_login",
            user_id: user.userId || "unknown",
            method: "email",
          });
          setSuccessMessage(response.data.successMessage);
          // Check for redirectAfterLogin preference
          const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");
          if (redirectAfterLogin === "dream-job") {
            navigate("/candidate-dashboard/jobs/dream-job", {
              state: { userId: user.userId, userName: user.userName },
            });
          } else if (redirectAfterLogin === "dream-company") {
            navigate(
              `/candidate-dashboard/companies/companyPage/companyName/${companyName}`,
              {
                state: {
                  userId: user.userId,
                  userName: user.userName,
                  companyId: companyId,
                },
              }
            );
          } else {
            navigate("/candidate-dashboard", {
              state: { userId: user.userId, userName: user.userName },
            });
          }
          // Clear the redirect target once used
          localStorage.removeItem("redirectAfterLogin");
        } else {
          setErrorMessage(
            "Invalid login credentials or role. Please try again."
          );
        }
      } else {
        setErrorMessage("Invalid email or password.");
      }
    } catch (error) {
      // In case of an error response (e.g., 401, 500, etc.), handle it here.

      // If the error is related to HTTP (status code != 2xx), we handle it accordingly
      if (error.response) {
        // Check if there's an error message in the response
        const errorMessage =
          error.response.data.errorMessage ||
          "An error occurred during login. Please try again.";
        setErrorMessage(errorMessage);
      } else {
        // In case of network issues or other errors, show a generic message
        setErrorMessage("An error occurred during login. Please try again.");
      }
    } finally {
      // Close the loading popup after the process is complete
      Swal.close();
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    await handleUserActivation(values);
    setSubmitting(false);
  };

  // Google Sign-In Handle
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    let loadingPopup;
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userEmail = user.email;

      // Check if the user exists in the backend
      const response = await api.checkUserWithToken(userEmail);
      const existingUser = response.data.user;
      const token = response.data.token;

      if (existingUser && token) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(existingUser));

        // Store user data and token in cookies
        const domain =
          window.location.hostname === "localhost" ? "localhost" : "jobbox.one";
        document.cookie = `user=${JSON.stringify(
          existingUser
        )}; path=/; domain=${domain}; secure; samesite=strict;`;
        document.cookie = `token=${token}; path=/; domain=${domain}; secure; samesite=strict;`;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "candidate_login",
          user_id: existingUser.userId || "unknown",
          method: "google",
        });
        // Navigate to dashboard
        Swal.fire({
          icon: "success",
          title: "Welcome Back!",
          text: "You are already registered. Click here to go to your dashboard.",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/candidate-dashboard", {
              state: {
                userId: existingUser.userId,
                userName: existingUser.userName,
              },
            });
          }
        });
      } else {
        // Show loading message using SweetAlert
        loadingPopup = Swal.fire({
          title: "Signing up...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Handle new user registration
        const randomPassword = Math.random().toString(36).slice(-8);
        const newUser = {
          userName: user.displayName, // Ensure userName is set
          userEmail: userEmail,
          password: randomPassword,
          userRole: "Candidate",
          appliedDate: getFormattedDate(),
        };

        // Save new user to the backend
        const saveResponse = await api.saveUser(newUser);

        if (saveResponse.status === 201) {
          const response = await api.checkUserWithToken(userEmail);
          const registeredUser = response.data.user;
          const token = response.data.token;

          if (registeredUser && token) {
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(registeredUser));

            const domain =
              window.location.hostname === "localhost"
                ? "localhost"
                : "jobbox.one";
            document.cookie = `user=${JSON.stringify(
              registeredUser
            )}; path=/; domain=${domain}; secure; samesite=strict;`;
            document.cookie = `token=${token}; path=/; domain=${domain}; secure; samesite=strict;`;

            Swal.fire({
              icon: "success",
              title: "Registration Successful!",
              text: "Your account has been created. Click here to go to your dashboard.",
              confirmButtonText: "OK",
            }).then((result) => {
              if (result.isConfirmed) {
                navigate("/candidate-dashboard", {
                  state: {
                    userId: registeredUser.userId,
                    userName: registeredUser.userName,
                  },
                });
              }
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Registration Failed!",
              text: "An error occurred while creating your account. Please try again.",
              confirmButtonText: "OK",
            });
          }
        }
      }
    } catch (error) {
      setErrorMessage("Error signing in with Google.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  function getFormattedDate() {
    const appliedOn = new Date(); // Get current date and time
    const year = appliedOn.getFullYear(); // Get the full year (e.g., 2024)
    const month = String(appliedOn.getMonth() + 1).padStart(2, "0"); // Get month (January is 0, so we add 1)
    const day = String(appliedOn.getDate()).padStart(2, "0"); // Get day of the month
    return `${year}-${month}-${day}`;
  }
  // Facebook Sign-In Handler
  // const signInWithFacebook = async () => {
  //     const provider = new FacebookAuthProvider();
  //     try {
  //         const result = await signInWithPopup(auth, provider);
  //         const user = result.user;
  //         // You can store user info in localStorage or handle it as needed
  //         localStorage.setItem('user', JSON.stringify(user));
  //         // Navigate or perform other actions as needed
  //     } catch (error) {
  //         setErrorMessage("Error signing in with Facebook.");
  //     }
  // };

  const signInWithFacebook = async () => {
    const provider = new FacebookAuthProvider();
    try {
      // Show Facebook login popup
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userEmail = user.email;
      // Check if the user already exists in the backend
      const response = await api.checkUser(userEmail);
      const existingUser = response.data;
      if (existingUser) {
        const userId = existingUser.userId;
        // Show SweetAlert for existing user
        Swal.fire({
          icon: "success",
          title: "Welcome Back!",
          text: "You are already registered. click here to go your dashboard.",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/candidate-dashboard", {
              state: {
                userRole: "Candidate",
                userId: userId,
                userName: user.userName,
              },
            });
          }
        });
      } else {
        // User does not exist, create a new user
        const randomPassword = Math.random().toString(36).slice(-8);
        const newUser = {
          userName: user.displayName, // Use Facebook user display name
          userEmail: userEmail,
          password: randomPassword,
          userRole: "Candidate",
          appliedDate: getFormattedDate(),
        };
        // Save new user to the backend
        const saveResponse = await api.saveUser(newUser);
        // Log the entire save response to see what is returned from the backend
        // Check if the user is successfully created
        if (saveResponse.data) {
          Swal.fire({
            icon: "success",
            title: "Registration Successful!",
            text: "Your registration has been successful. Welcome! Click here to set password.",
            confirmButtonText: "OK",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate("/forgetpassword", { state: { userRole: "Candidate" } });
            }
          });
        }
      }
    } catch (error) {
      // Log detailed error information
      setErrorMessage("Error signing in with Facebook. Please try again.");
    }
  };
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // const navLinkStyle = screenWidth > 990 ? { marginRight: '40px', marginLeft: '150px' } : {};
  const homeUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:3001"
      : "https://jobbox.one";

  return (
    <div
      className="auth-layout-wrap"
      style={{
        backgroundImage:
          'linear-gradient(rgba(5,15,35,0.72), rgba(0,10,30,0.78)), url("/candidate.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
      }}
    >
      <div className="auth-content d-flex align-items-center justify-content-center min-vh-100">
        <Card className="o-hidden card bg-transparent border-0 shadow-none">
          <Row className="g-0 align-items-center">
            <Col md={6}>
              <div
                className="p-4 p-md-5"
              >
                <div className="auth-logo text-center mb-4">
                  <a href={homeUrl}>
                    <img
                      src="/jb_logo.png"
                      alt="jobbox_logo"
                      style={{ maxWidth: "180px" }}
                    />
                  </a>
                </div>

                <h1
                  className="mb-4 fw-bold text-center"
                  style={{
                    color: "#ffffff",
                    fontSize: "28px",
                    letterSpacing: "0.5px",
                    textShadow: "0 0 10px rgba(0,180,255,0.6)",
                  }}
                >
                  Candidate Login
                </h1>

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <TextField
                        fullWidth
                        margin="normal"
                        type="email"
                        name="userEmail"
                        label="Email Address"
                        onBlur={handleBlur}
                        value={values.userEmail}
                        onChange={handleChange}
                        helperText={errors.userEmail}
                        error={errors.userEmail && touched.userEmail}
                        InputLabelProps={{
                          style: { color: "#d8ecff" },
                        }}
                        InputProps={{
                          style: {
                            color: "#fff",
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: "10px",
                          },
                        }}
                      />

                      <div className="position-relative mt-3">
                        <TextField
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          name="password"
                          label="Password"
                          onBlur={handleBlur}
                          value={values.password}
                          onChange={handleChange}
                          helperText={errors.password}
                          error={errors.password && touched.password}
                          InputLabelProps={{
                            style: { color: "#d8ecff" },
                          }}
                          InputProps={{
                            style: {
                              color: "#fff",
                              background: "rgba(255,255,255,0.08)",
                              borderRadius: "10px",
                            },
                          }}
                        />

                        <button
                          type="button"
                          className="btn btn-link position-absolute"
                          style={{
                            right: "18px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            padding: "0",
                            color: "#9fdcff",
                            background: "transparent",
                            border: "none",
                          }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <FaEyeSlash size={18} />
                          ) : (
                            <FaEye size={18} />
                          )}
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn btn-rounded btn-primary w-100 my-1 mt-2"

                      >
                        {isSubmitting ? "Signing In..." : "Login"}
                      </button>
                    </Form>
                  )}
                </Formik>

                <div className="fw-semibold mt-4 text-center">
                  <Link
                    to="/forget-password"
                    state={{ redirectPath: "/signin" }}
                    style={{
                      color: "#cfe9ff",
                      textDecoration: "none",
                    }}
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
            </Col>

            <Col md={6} className="text-center auth-cover">
              <div className="pe-3 auth-right">
                {loading ? (
                  <div style={{ color: "#fff" }}>Loading...</div>
                ) : (
                  <SocialButtons
                    isLogin={false}
                    routeUrl="/candidate-signup"
                    googleHandler={signInWithGoogle}
                    facebookHandler={signInWithFacebook}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {showActivationModal && (
        <ActivataionAccount
          userEmail={userEmail}
          onClose={() => setShowActivationModal(false)}
        />
      )}
    </div>
  );
};
export default UserSignin;
